import { PayloadAction } from '@reduxjs/toolkit';
import { WeeklyPlannerParams } from '~weekly-planner/types/params';
import dayjs from 'dayjs';

import { dateInputFieldFormat } from '~libs/dayjs';
import { get, set } from '~libs/localstorage';
import { buildSlice, CommonState, Row } from '~libs/reduxUtils';
import { UnallocatedAppointment } from '~weekly-planner/lib/common';

import { typePrefix } from '~weekly-planner/actions';

interface State extends CommonState<Row> {
  params: WeeklyPlannerParams;
  previousParams: Partial<WeeklyPlannerParams>;
  searchParams?: { [key: string]: any };
  options: { [key: string]: boolean };
  optionRequiresRebuild: boolean;
  openAppointmentDetailsId: number | null;
  openAppointmentRosteringNotesId: number | null;
  focusedWorkerId: number | null;
  focused: {
    appointment: UnallocatedAppointment | null;
    isBiddingView: boolean;
    newBidders: string[];
  };
  focusOptions: {
    availabilityFilter: 'All' | 'Available' | 'Not Available';
    qualifiedFilter: 'All' | 'Yes' | 'No';
    highlightType: 'Times' | 'Distance';
    preferenceFilter: Array<'Preferred' | 'Historical'>;
    sortBy: 'Alphabetical' | 'Bidding';
  };
  orderBy: {
    availability: boolean;
    distance: boolean;
  };
  publishDate: string;
  importedAppointments?: {
    id?: number | null;
    user_id?: number;
    date?: string;
    parent_repeat_id?: number;
  };
}

const initialState: State = {
  loading: 'idle',
  hasLoaded: false,
  error: null,
  rows: [],
  params: {
    date: dayjs().format(dateInputFieldFormat),
    viewType: 'Users',
    rangeType: 'Weekly',
    limit: 30,
    refetchKey: 0,
  },
  previousParams: {},
  options: {
    workerAvailability: false,
    workerQualifications: false,
    appointmentFlexibility: false,
    showBreaks: true,
    showTravel: true,
    showServiceType: true,
    showCancellations: true,
    showPayableCancellations: true,
    showCardQualifications: false,
    showSystemUsers: false,
    workerAwardAlerts: false,
    totalSimple: false,
    totalAppointments: false,
    totalPredicted: true,
    totalDistance: true,
    ...get('weekly-planner-view-options'),
  },
  optionRequiresRebuild: false,
  openAppointmentDetailsId: null,
  openAppointmentRosteringNotesId: null,
  focusedWorkerId: null,
  focused: {
    appointment: null,
    isBiddingView: false,
    newBidders: [],
  },
  focusOptions: {
    qualifiedFilter: 'Yes',
    availabilityFilter: 'Available',
    preferenceFilter: [],
    highlightType: 'Times',
    sortBy: 'Alphabetical',
  },
  orderBy: {
    availability: false,
    distance: false,
  },
  publishDate: '',
  importedAppointments: {},
};

const buildAffectedOptions = [
  'showCancellations',
  'showPayableCancellations',
  'showTravel',
  'showBreaks',
  'showTeaBreaks',
];

const slice = buildSlice<State>(typePrefix, initialState, {
  customActions: {
    toggleOption(state, action) {
      const { key, toggle } = action.payload;
      state.options[key] = toggle;
      set('weekly-planner-view-options', state.options);
      if (buildAffectedOptions.includes(key)) {
        state.optionRequiresRebuild = true;
      }
    },
    setOptionRequiresRebuild(state, action) {
      state.optionRequiresRebuild = action.payload;
    },
    openAppointmentDetails(state, action) {
      if (!action.payload) {
        state.openAppointmentDetailsId = null;
        return;
      }
      state.openAppointmentDetailsId = action.payload;
    },
    openAppointmentRosteringNotes(state, action) {
      if (!action.payload) {
        state.openAppointmentRosteringNotesId = null;
        return;
      }
      state.openAppointmentRosteringNotesId = action.payload;
    },
    focusWorker(state, action) {
      state.focusedWorkerId = action.payload;
    },
    setParams(state, action: { payload: WeeklyPlannerParams }) {
      state.params = action.payload;
    },
    setSearchParams(state, action) {
      state.searchParams = action.payload;
    },
    storeViewState(state) {
      const { date, viewType, rangeType } = state.params;
      state.previousParams = { date, viewType, rangeType };
    },
    restoreViewState(state) {
      if (state.previousParams) {
        state.params = { ...state.params, ...state.previousParams };
      }
      state.previousParams = {};
    },
    unfocusAppointment(state) {
      state.focused = { appointment: null, isBiddingView: false, newBidders: [] };
      state.focusedWorkerId = null;

      // Restore previous params
      if (state.previousParams) {
        state.params = { ...state.params, ...state.previousParams };
        state.previousParams = {};
      }

      // Clear focus related params
      state.params = {
        ...state.params,
        focus_appointment_id: null,
        focus_worker_id: null,
        filter_by: {
          ...(state.params.filter_by ?? {}),
          focused: null,
        },
        refetchKey: (state.params.refetchKey ?? 0) + 1,
      };
    },
    focusAppointment(state, action) {
      const { payload } = action;
      if (!payload || !payload.focused) return;

      const { focused, params: focusedParams, focusedWorkerId, focusOptions = {} } = payload;
      const { appointment, isBiddingView = false, newBidders = [] } = focused;

      // Stash params
      if (!state.previousParams) {
        const { date, viewType, rangeType, limit, filter_by, sort_by, focus_appointment_id, focus_worker_id } =
          state.params;
        state.previousParams = {
          date,
          viewType,
          rangeType,
          limit,
          filter_by,
          sort_by,
          focus_appointment_id,
          focus_worker_id,
        };
      }

      // Apply focused UI state
      state.focused = { appointment: appointment ?? null, isBiddingView, newBidders };
      state.focusOptions = { ...initialState.focusOptions, ...focusOptions };
      state.focusedWorkerId = focusedWorkerId ?? null;

      // Write focused params
      if (focusedParams) {
        state.params = { ...state.params, ...focusedParams };
      }
    },
    toggleBidding(state, action) {
      const { isBiddingView } = action.payload;
      state.focused = { ...state.focused, isBiddingView };
    },

    updateFocusOptions(state, action) {
      const options = action.payload;

      for (const key of Object.keys(options)) {
        state.focusOptions = {
          ...state.focusOptions,
          [key]: options[key],
        };
      }
    },
    clearFocusOptions(state) {
      state.focusOptions = initialState.focusOptions;
    },
    update(state, action) {
      const options = action.payload;
      for (const key of Object.keys(options)) {
        state[key] = options[key];
      }
    },
    patchParams(state, action: PayloadAction<Partial<WeeklyPlannerParams>>) {
      const prevViewType = state.params.viewType;
      const prevRangeType = state.params.rangeType;
      const prevDate = state.params.date;

      state.params = { ...state.params, ...action.payload };

      const viewTypeChanged = action.payload.viewType !== undefined && action.payload.viewType !== prevViewType;
      const rangeTypeChanged = action.payload.rangeType !== undefined && action.payload.rangeType !== prevRangeType;
      const dateChanged = action.payload.date !== undefined && action.payload.date !== prevDate;

      if ((viewTypeChanged || rangeTypeChanged || dateChanged) && action.payload.limit === undefined) {
        state.params.limit = 30;
      }
    },
    updatePublishDate(state, action) {
      state.publishDate = action.payload;
    },
    updateBiddingWorkers(state, action) {
      // TODO: rename func for clarity - updateUnsavedBiddingWorkers ?
      const { workerIds, isChecked } = action.payload;

      if (!state.focused.appointment) return;

      const newBidderIds = state.focused.newBidders || [];

      const mergedBidderIds = isChecked
        ? [...new Set([...newBidderIds, ...workerIds])]
        : newBidderIds.filter((id: any) => !workerIds.includes(id));

      state.focused = {
        ...state.focused,
        newBidders: mergedBidderIds,
      };
    },
    mergeBiddingWorkers(state) {
      const { focused } = state;
      const { appointment, newBidders } = focused || {};
      const existingBidders = appointment?.bidding_user_ids ?? [];
      const mergedBidders = Array.from(new Set([...existingBidders, ...newBidders]));

      const updatedAppointment: Partial<UnallocatedAppointment> = {
        ...appointment,
        is_bidding: true,
        bidding_user_ids: mergedBidders,
      };

      state.focused = { ...focused, appointment: updatedAppointment as UnallocatedAppointment, newBidders: [] };
    },
    resetWeeklyPlannerState(state) {
      // Reset the weekly planner state to initial values
      Object.assign(state, initialState);
      state.options = {
        ...initialState.options,
        ...get('weekly-planner-view-options'),
      };
      state.openAppointmentDetailsId = null;
    },
    bumpRefetchKey(state) {
      state.params.refetchKey = (state.params.refetchKey ?? 0) + 1;
    },
  },
});

export const {
  clear,
  clearFocusOptions,
  update,
  updateFocusOptions,
  updatePublishDate,
  toggleOption,
  setOptionRequiresRebuild,
  openAppointmentDetails,
  openAppointmentRosteringNotes,
  focusWorker,
  focusAppointment,
  toggleBidding,
  updateBiddingWorkers,
  mergeBiddingWorkers,
  updateWithPersistence,
  resetWeeklyPlannerState,
  storeViewState,
  restoreViewState,
  unfocusAppointment,
  setSearchParams,
  patchParams,
  bumpRefetchKey,
} = slice.actions;

export default slice.reducer;
